import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from "oidc-react";
import { Dashboard, useDashboard, useDashboards, updateFavoriteDashboard, updateDefaultDashboard } from '../../api/dashboards'
import { CompanyConfig, useCompanyConfig } from '../../api/companyConfig';
import useSavedField from '../../helpers/useSavedField';

export const REFRESH_COOLDOWN_IN_MILLIS = 3 * 60 * 1000;

export const REFRESH_DASHBOARD_KEY = 'refreshDashboardCooldown';

type DashboardContextType = {
  currentDashboard: Dashboard | undefined;
  dashboardId: string;
  loadingDashboard: boolean;
  isFavourite: boolean;
  isDefault: boolean;
  companyConfig: CompanyConfig | undefined;
  dashboards: Dashboard[] | undefined;
  isLoadingDashboards: boolean;
  isErrorDashboards: boolean;
  isLoadingDashboard: boolean;
  refreshDate?: number;
  fetchCompanyConfig: () => void;
  setCurrentDashboard: (dashboard: Dashboard) => void;
  updateDashboard: () => void;
  fetchDasboards: () => void;
  toggleFavourite: () => void;
  toggleDefault: () => void;
  refreshDashboard: () => void;
};

const dummyDashboard = {
  currentDashboard: undefined,
  dashboardId: '',
  loadingDashboard: true,
  isFavourite: false,
  isDefault: false,
  companyConfig: {} as CompanyConfig,
  dashboards: [],
  isLoadingDashboards: true,
  isErrorDashboards: false,
  isLoadingDashboard: true,
  refreshDate: Date.now(),
  fetchCompanyConfig: () => { /* NO OP */ },
  setCurrentDashboard: () => { /* NO OP */ },
  updateDashboard: () => { /* NO OP */ },
  fetchDasboards: () => { /* NO OP */ },
  toggleFavourite: () => { /* NO OP */ },
  toggleDefault: () => { /* NO OP */ },
  refreshDashboard: () => { /* NO OP */ }
};

const DashboardContext = createContext<DashboardContextType>(dummyDashboard);

const DashboardContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [refreshDate, setRefreshDate] = useState<number>();
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;

  const {
    val: currentDashboard,
    setVal: internalSetCurrentDashboard,
    apply: internalChangeDashboard
} = useSavedField<Dashboard | undefined>(undefined);

const refreshDashboard = useCallback(() => {
  const refreshCooldown = parseInt(localStorage.getItem(REFRESH_DASHBOARD_KEY) || '0', 10);
  if(refreshCooldown < Date.now()) {
    setRefreshDate(Date.now());
    localStorage.setItem(REFRESH_DASHBOARD_KEY, `${Date.now() + REFRESH_COOLDOWN_IN_MILLIS}`);
  }
}, []);

const setCurrentDashboard = useCallback((dashboard: Dashboard) => {
  internalSetCurrentDashboard(dashboard);
  refreshDashboard();
}, [internalSetCurrentDashboard]);

const {
    data: dashboards,
    isLoading: isLoadingDashboards,
    isError: isErrorDashboards,
    refetch: fetchDasboards
  } = useDashboards<Dashboard[]>('standard');

  const {
    data: currentDashboardInternal,
    isLoading: isLoadingDashboard,
    isError: isErrorDashboard,
    refetch: updateDashboard
  } = useDashboard<Dashboard>('dashboard', currentDashboard?.dashboard?.id || '', 
    {
      enabled: false
    }
  );

  const toggleFavourite = useCallback(() => 
  updateFavoriteDashboard(
    currentDashboard?.dashboard?.id,
    !currentDashboard?.dashboard?.favourite,
    maybeAccessToken
  ).then(() => {
    internalSetCurrentDashboard((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        dashboard: {
          ...prev.dashboard,
          favourite: !prev.dashboard.favourite,
        },
      };
    });
    fetchDasboards();
  }
  )
  .catch((err) => {/* no op */})
, [currentDashboard, maybeAccessToken]);

const toggleDefault = useCallback(() => 
  updateDefaultDashboard(
    currentDashboard?.dashboard?.id,
    !currentDashboard?.dashboard?.isDefault,
    maybeAccessToken
  ).then(() => {
    internalSetCurrentDashboard((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        dashboard: {
          ...prev.dashboard,
          isDefault: !prev.dashboard.isDefault,
        },
      };
    });
    fetchDasboards();
  }
  )
  .catch((err) => {/* no op */})
, [currentDashboard, maybeAccessToken]);

  useEffect(() => {
    if(currentDashboardInternal?.dashboard){
      setCurrentDashboard(currentDashboardInternal);
    }
  }, [currentDashboardInternal]);

  const { data: companyConfig, refetch: fetchCompanyConfig } = useCompanyConfig(
    `companyConfig`
  );

  return (
    <DashboardContext.Provider
      value={{
        currentDashboard,
        dashboardId: currentDashboard?.dashboard?.id || '',
        loadingDashboard,
        isFavourite: currentDashboard?.dashboard?.favourite || false,
        isDefault: currentDashboard?.dashboard?.isDefault || false,
        companyConfig,
        dashboards,
        isLoadingDashboards,
        isErrorDashboards,
        isLoadingDashboard,
        refreshDate,
        fetchCompanyConfig,
        setCurrentDashboard,
        updateDashboard,
        fetchDasboards,
        toggleFavourite,
        toggleDefault,
        refreshDashboard
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export {
  DashboardContext,
  DashboardContextProvider
}
